/**
 * Types pour le système de notifications frontend
 */

// ============================================================================
// Enums (miroir du backend)
// ============================================================================

export type NotificationType =
  // Fiscalité
  | 'VAT_DECLARATION_CREATED'
  | 'VAT_DECLARATION_READY'
  | 'VAT_DECLARATION_TRANSMITTED'
  | 'VAT_DECLARATION_ACCEPTED'
  | 'VAT_DECLARATION_REJECTED'
  | 'VAT_PERIOD_CLOSING_SOON'
  | 'VAT_DEADLINE_REMINDER'
  // Facturation
  | 'INVOICE_CREATED'
  | 'INVOICE_SENT'
  | 'INVOICE_VIEWED'
  | 'INVOICE_PAID'
  | 'INVOICE_OVERDUE'
  | 'INVOICE_PAYMENT_FAILED'
  | 'QUOTE_CREATED'
  | 'QUOTE_SENT'
  | 'QUOTE_ACCEPTED'
  | 'QUOTE_REJECTED'
  | 'QUOTE_EXPIRING_SOON'
  | 'CREDIT_NOTE_CREATED'
  // Paiements
  | 'PAYMENT_RECEIVED'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_REMINDER_SENT'
  | 'STRIPE_PAYOUT_COMPLETED'
  // Trésorerie
  | 'CASH_FLOW_ALERT'
  | 'CASH_BALANCE_LOW'
  | 'CASH_FORECAST_UPDATED'
  | 'BANK_SYNC_COMPLETED'
  | 'BANK_SYNC_FAILED'
  | 'RECONCILIATION_SUGGESTED'
  // IA / Conseils (CFO Virtuel)
  | 'AI_RECOMMENDATION'
  | 'AI_ANOMALY_DETECTED'
  | 'AI_OPTIMIZATION_SUGGESTION'
  | 'AI_RISK_ALERT'
  | 'AI_DAILY_SUMMARY'
  // Phase 6 - CFO Virtuel Intelligence
  | 'AI_CASHFLOW_PREDICTION'      // Prédiction trésorerie proactive
  | 'AI_PAYMENT_RISK_ALERT'       // Risque impayé client détecté
  | 'AI_ACTION_SUGGESTED'         // Action suggérée (queue approbation)
  | 'AI_ACTION_EXECUTED'          // Action auto exécutée
  | 'AI_ACTION_FAILED'            // Action auto échouée
  | 'AI_WEEKLY_SUMMARY'           // Résumé hebdomadaire
  | 'AI_SIMULATION_READY'         // Simulation terminée
  | 'AI_BENCHMARK_INSIGHT'        // Insight benchmark sectoriel
  | 'AI_NETWORK_ALERT'            // Alerte réseau client (cross-tenant)
  | 'AI_OPPORTUNITY_DETECTED'     // Opportunité détectée
  | 'AI_HEALTH_SCORE_CHANGE'      // Changement score santé financière
  // Système
  | 'SYSTEM_MAINTENANCE'
  | 'SYSTEM_UPDATE'
  | 'SECURITY_ALERT'
  | 'SESSION_EXPIRED'
  // Collaboration
  | 'USER_MENTIONED'
  | 'DOCUMENT_SHARED'
  | 'COMMENT_ADDED'
  | 'TASK_ASSIGNED';

export type NotificationCategory =
  | 'FISCAL'
  | 'BILLING'
  | 'PAYMENTS'
  | 'TREASURY'
  | 'AI_INSIGHTS'
  | 'SYSTEM'
  | 'COLLABORATION'
  | 'SECURITY';

export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export type NotificationChannel = 'IN_APP' | 'EMAIL' | 'PUSH' | 'SMS';

// ============================================================================
// Core Types
// ============================================================================

export interface ActionButton {
  label: string;
  action: 'navigate' | 'api_call' | 'dismiss' | 'external_link';
  style: 'primary' | 'secondary' | 'danger' | 'ghost';
  url?: string;
  apiEndpoint?: string;
  apiMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

export interface Notification {
  id: string;
  tenantId: string;
  userId: string;
  type: NotificationType;
  category: NotificationCategory;
  priority: NotificationPriority;
  title: string;
  message: string;
  richContent?: Record<string, unknown>;
  link?: string;
  actionButtons?: ActionButton[];
  sourceModule?: string;
  sourceEntityId?: string;
  sourceEntityType?: string;
  isRead: boolean;
  readAt?: string;
  isDismissed: boolean;
  dismissedAt?: string;
  isArchived: boolean;
  archivedAt?: string;
  channels: NotificationChannel[];
  metadata?: Record<string, unknown>;
  aiGenerated: boolean;
  aiConfidence?: number;
  groupKey?: string;
  groupCount: number;
  expiresAt?: string;
  scheduledFor?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byCategory: Partial<Record<NotificationCategory, number>>;
  byPriority: Partial<Record<NotificationPriority, number>>;
  today: number;
  thisWeek: number;
}

export interface PaginatedNotifications {
  data: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// ============================================================================
// Query & Filter Types
// ============================================================================

export interface NotificationFilters {
  category?: NotificationCategory;
  type?: NotificationType;
  isRead?: boolean;
  isDismissed?: boolean;
  isArchived?: boolean;
  sourceModule?: string;
  priority?: NotificationPriority;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'priority' | 'readAt';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// Preference Types
// ============================================================================

export type EmailDigestFrequency = 'INSTANT' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'NEVER';

export interface NotificationPreference {
  id: string;
  type: NotificationType;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  emailDigest: EmailDigestFrequency;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  quietDays: number[];
}

// ============================================================================
// UI Helper Types
// ============================================================================

export interface CategoryInfo {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}

export const CATEGORY_INFO: Record<NotificationCategory, CategoryInfo> = {
  FISCAL: {
    label: 'Fiscalité',
    icon: 'FileText',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  BILLING: {
    label: 'Facturation',
    icon: 'Receipt',
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
  PAYMENTS: {
    label: 'Paiements',
    icon: 'CreditCard',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  TREASURY: {
    label: 'Trésorerie',
    icon: 'Wallet',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  AI_INSIGHTS: {
    label: 'Conseils IA',
    icon: 'Sparkles',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  SYSTEM: {
    label: 'Système',
    icon: 'Settings',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
  COLLABORATION: {
    label: 'Équipe',
    icon: 'Users',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  SECURITY: {
    label: 'Sécurité',
    icon: 'Shield',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};

export const PRIORITY_INFO: Record<NotificationPriority, { label: string; color: string }> = {
  LOW: { label: 'Basse', color: 'text-gray-500' },
  NORMAL: { label: 'Normale', color: 'text-blue-500' },
  HIGH: { label: 'Haute', color: 'text-orange-500' },
  URGENT: { label: 'Urgente', color: 'text-red-500' },
};

// ============================================================================
// AI Notification Payloads (Phase 6 - CFO Virtuel)
// ============================================================================

/**
 * Niveau de sévérité pour les insights IA
 */
export type AIInsightSeverity = 'info' | 'warning' | 'critical';

/**
 * Type d'insight IA
 */
export type AIInsightType =
  | 'prediction'    // Prédiction (cashflow, risque)
  | 'anomaly'       // Anomalie détectée
  | 'suggestion'    // Suggestion d'action
  | 'benchmark'     // Comparaison sectorielle
  | 'opportunity'   // Opportunité business
  | 'summary';      // Résumé périodique

/**
 * Action suggérée par l'IA
 */
export interface AISuggestedAction {
  id: string;
  label: string;
  description?: string;
  type: 'navigate' | 'api_call' | 'approve' | 'reject' | 'simulate' | 'dismiss';
  style: 'primary' | 'secondary' | 'danger' | 'ghost';
  /** URL de navigation ou endpoint API */
  target?: string;
  /** Méthode HTTP si api_call */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Payload pour l'API */
  payload?: Record<string, unknown>;
  /** Impact estimé de l'action (ex: "+4,200€") */
  estimatedImpact?: string;
  /** Nécessite approbation utilisateur */
  requiresApproval: boolean;
}

/**
 * Base commune pour tous les payloads IA
 */
export interface AINotificationPayloadBase {
  insightType: AIInsightType;
  confidence: number;           // 0-100%
  severity: AIInsightSeverity;
  suggestedActions: AISuggestedAction[];
  /** Date d'expiration de la pertinence */
  expiresAt?: string;
  /** Source du calcul: 'ml_worker' | 'heuristics' */
  provider: 'ml_worker' | 'heuristics' | 'openai';
  /** Données anonymisées utilisées */
  anonymized: boolean;
}

/**
 * Payload pour AI_CASHFLOW_PREDICTION
 */
export interface AICashflowPredictionPayload extends AINotificationPayloadBase {
  insightType: 'prediction';
  prediction: {
    daysUntilCritical: number;
    predictedBalance: number;
    criticalDate: string;
    optimisticBalance: number;
    pessimisticBalance: number;
  };
  causes: Array<{
    type: 'invoice_at_risk' | 'grouped_expenses' | 'no_expected_income' | 'seasonal_pattern';
    description: string;
    amount?: number;
    entityId?: string;
    entityType?: string;
    riskPercentage?: number;
  }>;
}

/**
 * Payload pour AI_PAYMENT_RISK_ALERT
 */
export interface AIPaymentRiskPayload extends AINotificationPayloadBase {
  insightType: 'prediction';
  client: {
    id: string;
    name: string;
  };
  invoice?: {
    id: string;
    number: string;
    amount: number;
    dueDate: string;
  };
  riskScore: number;            // 0-100 (100 = risque max)
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskFactors: Array<{
    factor: string;
    impact: number;             // Points de risque
    description: string;
  }>;
  historicalData: {
    avgPaymentDays: number;
    onTimeRate: number;         // 0-100%
    totalOverdue: number;
  };
}

/**
 * Payload pour AI_ACTION_SUGGESTED / AI_ACTION_EXECUTED
 */
export interface AIActionPayload extends AINotificationPayloadBase {
  insightType: 'suggestion';
  action: {
    id: string;
    type: 'reminder_email' | 'categorization' | 'reconciliation' | 'report' | 'other';
    targetEntity?: {
      type: string;
      id: string;
      label: string;
    };
    /** Pour les emails: contenu généré */
    generatedContent?: {
      subject?: string;
      body?: string;
      recipient?: string;
    };
  };
  /** Pour AI_ACTION_EXECUTED: résultat */
  result?: {
    success: boolean;
    executedAt: string;
    error?: string;
  };
  /** Niveau d'autonomie configuré */
  autonomyLevel: 'L1_SUGGESTION' | 'L2_DRAFT' | 'L3_AUTO_NOTIFY' | 'L4_FULL_AUTO';
}

/**
 * Payload pour AI_BENCHMARK_INSIGHT
 */
export interface AIBenchmarkPayload extends AINotificationPayloadBase {
  insightType: 'benchmark';
  metric: {
    name: string;               // Ex: "DSO", "Marge brute", "Taux impayés"
    yourValue: number;
    sectorAverage: number;
    sectorTop10: number;
    unit: string;               // Ex: "jours", "%", "€"
    trend: 'UP' | 'DOWN' | 'STABLE';
  };
  position: {
    percentile: number;         // Votre position (ex: 65 = top 35%)
    comparison: 'above' | 'below' | 'at' | 'significantly_above' | 'significantly_below';
    gap: number;                // Écart avec la moyenne
    gapPercentage: number;
  };
  bestPractices?: Array<{
    practice: string;
    adoptionRate: number;       // % des top performers qui l'utilisent
    yourStatus: 'adopted' | 'not_adopted' | 'partial';
  }>;
}

/**
 * Payload pour AI_NETWORK_ALERT
 */
export interface AINetworkAlertPayload extends AINotificationPayloadBase {
  insightType: 'anomaly';
  client: {
    id: string;
    name: string;
    /** Hash SIRET anonymisé pour corrélation réseau */
    networkHash?: string;
  };
  networkSignals: {
    /** Nombre d'entreprises du réseau signalant ce client */
    reportingCompanies: number;
    /** Délai moyen observé sur le réseau */
    networkAvgDelay: number;
    /** Évolution du délai */
    delayTrend: 'increasing' | 'stable' | 'improving';
    /** Litiges signalés */
    disputesReported: number;
  };
  yourExposure: {
    outstandingInvoices: number;
    pendingQuotes: number;
    totalAmount: number;
  };
}

/**
 * Payload pour AI_SIMULATION_READY
 */
export interface AISimulationPayload extends AINotificationPayloadBase {
  insightType: 'prediction';
  simulation: {
    id: string;
    name: string;
    scenarioType: 'hiring' | 'investment' | 'client_loss' | 'price_increase' | 'custom';
    requestedAt: string;
    completedAt: string;
  };
  summary: {
    breakEvenMonths?: number;
    roiYear1?: number;
    cashImpactMin: number;
    cashImpactMax: number;
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  };
}

/**
 * Payload pour AI_WEEKLY_SUMMARY
 */
export interface AIWeeklySummaryPayload extends AINotificationPayloadBase {
  insightType: 'summary';
  period: {
    start: string;
    end: string;
  };
  highlights: {
    revenue: { amount: number; vsLastWeek: number };
    expenses: { amount: number; vsLastWeek: number };
    cashflow: { current: number; trend: 'UP' | 'DOWN' | 'STABLE' };
    invoicesPaid: number;
    invoicesOverdue: number;
    quotesAccepted: number;
  };
  alerts: Array<{
    type: string;
    message: string;
    severity: AIInsightSeverity;
  }>;
  topRecommendations: Array<{
    title: string;
    impact: string;
    actionId: string;
  }>;
}

/**
 * Payload pour AI_OPPORTUNITY_DETECTED
 */
export interface AIOpportunityPayload extends AINotificationPayloadBase {
  insightType: 'opportunity';
  opportunity: {
    type: 'public_market' | 'financing' | 'negotiation' | 'hiring' | 'other';
    title: string;
    description: string;
    estimatedValue?: number;
    deadline?: string;
    compatibilityScore?: number;  // 0-100%
  };
  details?: Record<string, unknown>;
}

/**
 * Payload pour AI_HEALTH_SCORE_CHANGE
 */
export interface AIHealthScorePayload extends AINotificationPayloadBase {
  insightType: 'anomaly';
  healthScore: {
    current: number;
    previous: number;
    change: number;
    trend: 'UP' | 'DOWN' | 'STABLE';
  };
  components: {
    liquidity: { score: number; weight: number };
    profitability: { score: number; weight: number };
    collection: { score: number; weight: number };
    expenseManagement: { score: number; weight: number };
  };
  mainFactors: Array<{
    component: string;
    change: number;
    reason: string;
  }>;
}

/**
 * Union type pour tous les payloads AI
 */
export type AINotificationPayload =
  | AICashflowPredictionPayload
  | AIPaymentRiskPayload
  | AIActionPayload
  | AIBenchmarkPayload
  | AINetworkAlertPayload
  | AISimulationPayload
  | AIWeeklySummaryPayload
  | AIOpportunityPayload
  | AIHealthScorePayload;

/**
 * Type guard pour vérifier si une notification est de type IA
 */
export function isAINotification(notification: Notification): boolean {
  return notification.type.startsWith('AI_');
}

/**
 * Type guard pour extraire le payload IA typé
 */
export function getAIPayload<T extends AINotificationPayload>(
  notification: Notification
): T | null {
  if (!isAINotification(notification) || !notification.richContent) {
    return null;
  }
  return notification.richContent as unknown as T;
}
